import React, { useEffect } from 'react';
import useIonicToast from '../hooks/useIonicToast';
import { usePageLoader } from '../context/pageLoader';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';

import { useQuery } from '@tanstack/react-query';
import { getUserProfile } from '../services/authentication/getUserProfile';
import { setUserProfile } from '../store/slices/userProfile/userProfileSlice';
import { useHistory, useLocation } from 'react-router';
import { sendVerificationCodeToEmail } from '../services/authentication/signup';
import { TermsGate } from 'ionic-ui';
import { baseURL } from '../data/config';

// import { toast } from 'react-toastify';
// import { SK_userSession } from '../data/config';

const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const route = useHistory();
  const location = useLocation();
  const { presentIonToast } = useIonicToast();
  const currentLocation = location.pathname;

  const { setBusy } = usePageLoader();
  const { signedAccessToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const dispatch = useDispatch();
  const userProfile = useSelector((state: RootState) => state.userProfile);
  const [dismissedReminder, setDismissedReminder] = React.useState(false);

  const reminderKey = React.useMemo(() => {
    const email = userProfile?.user?.email?.toLowerCase();
    return email ? `email-verification-reminder-dismissed:${email}` : null;
  }, [userProfile?.user?.email]);

  React.useEffect(() => {
    if (!signedAccessToken && !currentLocation.includes('/auth')) {
      return route.replace('/auth/sign-in');
    }
  }, [signedAccessToken]);

  const canLoadProfile =
    Boolean(signedAccessToken) && !currentLocation.includes('/auth');

  const { data, isSuccess, isLoading, isPending } = useQuery({
    queryKey: ['user-profile', signedAccessToken],
    enabled: canLoadProfile,
    queryFn: async () => {
      try {
        const res = await getUserProfile(signedAccessToken!);
        return res;
      } catch (error: any) {
        presentIonToast(error.message, 'error');
        if (error.status === 401) {
          dispatch({ type: 'auth/logout' });
          route.replace('/auth/sign-in');
        }
        throw error;
      } finally {
        setBusy(false);
      }
    },
  });

  useEffect(() => {
    setBusy(isLoading || (isPending && !currentLocation.includes('/auth')));
  }, [isLoading, canLoadProfile, isPending]);

  useEffect(() => {
    if (data && isSuccess) {
      dispatch(setUserProfile(data));
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (!reminderKey) {
      setDismissedReminder(false);
      return;
    }

    setDismissedReminder(localStorage.getItem(reminderKey) === 'true');
  }, [reminderKey]);

  const dismissVerificationReminder = () => {
    if (!reminderKey) return;
    localStorage.setItem(reminderKey, 'true');
    setDismissedReminder(true);
  };

  const resendVerificationEmail = async () => {
    if (!userProfile?.user?.email) return;
    try {
      await sendVerificationCodeToEmail(userProfile.user.email);
      presentIonToast('Verification email sent. Please check your inbox.');
    } catch (error: any) {
      presentIonToast(
        error.message || 'Unable to resend verification email',
        'error'
      );
    }
  };

  if (!signedAccessToken && !currentLocation.includes('/auth')) {
    return null;
  }

  const shouldShowReminder =
    Boolean(signedAccessToken) &&
    !currentLocation.includes('/auth') &&
    Boolean(userProfile?.user) &&
    !userProfile?.user?.is_verified &&
    !dismissedReminder;

  const showTermsGate =
    Boolean(signedAccessToken) && !currentLocation.includes('/auth');

  const handleTermsDecline = () => {
    dispatch({ type: 'auth/logout' });
    route.replace('/auth/sign-in');
  };

  return (
    <>
      {showTermsGate && (
        <TermsGate
          audience="store"
          accessToken={signedAccessToken}
          baseURL={baseURL}
          onDecline={handleTermsDecline}
        />
      )}
      {shouldShowReminder && (
        <div className="sticky top-0 z-40 px-3 pt-2">
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <p className="text-sm font-medium text-amber-900">
                Your email is not verified yet. Verify it to secure your account
                and avoid interruption.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white"
                  onClick={resendVerificationEmail}
                >
                  Resend email
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold"
                  onClick={dismissVerificationReminder}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {children}
    </>
  );
};

export default AuthWrapper;
